import { Button, Icon, Modal } from "@alm/flow-ds/components";
import useUploadMedia from "hooks/useUploadMedia";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
	title: string;
	onValidation: (file: string) => void;
	onUploaded?: () => void;
	acceptJson?: boolean;
	acceptXml?: boolean;
	btnLabel: string;
	file?: string;
	btnClass?: string;
	error?: string | null;
	isUploaded: boolean;
	clearError: () => void;
}

export default function UploadDialog({
	btnLabel,
	title,
	onValidation,
	onUploaded,
	acceptJson,
	acceptXml,
	file,
	btnClass,
	error,
	isUploaded,
	clearError,
}: Props) {
	const { t } = useTranslation();

	const [isUploadOpen, setIsUploadOpen] = useState(false);
	const [isWarningOpen, setIsWarningOpen] = useState(false);

	/**
	 * Validate function to pass into the useUploadMedia hook
	 */
	const validate = useCallback(
		file => {
			onValidation(file);
		},
		[onValidation]
	);

	const { getRootProps, getInputProps, isDragActive, open, isRejected } =
		useUploadMedia({
			acceptJson,
			acceptXml,
			validate,
		});

	/**
	 * Close modal and clear any errors on close
	 */
	const handleModalClose = useCallback(() => {
		setIsWarningOpen(false);
		clearError();
	}, [clearError]);

	// If there is an isUploaded prop provided then we call that
	// otherwise we will just close the modal
	useEffect(() => {
		if (isUploaded) onUploaded ? onUploaded() : setIsUploadOpen(false);
	}, [isUploaded, onUploaded]);

	// If isRejected (from the useUploadMedia) is true or if there is
	// and error (from the parent component) then we will open the warning modal
	// as it means there is a warning
	useEffect(() => {
		if (isRejected || error) {
			setIsWarningOpen(true);
		}
	}, [error, isRejected]);

	return (
		<>
			<Button
				label={btnLabel}
				className={`btn-primary ${btnClass ? btnClass : ""}`}
				onClick={() => setIsUploadOpen(true)}
			/>
			{!isWarningOpen && isUploadOpen && (
				<Modal
					className="upload-dialog"
					isOpen={isUploadOpen}
					title={title}
					close={() => setIsUploadOpen(false)}
					showHeaderClose={false}
					footer={
						<>
							<Button
								className="btn-outline-primary"
								label={t("common.labels.cancel")}
								onClick={() => setIsUploadOpen(false)}
							/>
							<Button
								className="btn-primary"
								onClick={open}
								label={t("formDesigner.labels.browseFiles")}
							/>
						</>
					}>
					<>
						<div
							{...getRootProps()}
							className={`upload-dialog__dropzone
                ${isDragActive && "upload-dialog__dropzone--dragOver"}`}>
							<input {...getInputProps()} />

							{isDragActive ? (
								<div className="upload-dialog__dropzone-content">
									<Icon
										type="file-upload"
										className="upload__dropzone-icon"
										size="3x"
										weight="d"
									/>
									<p className="mt-3 mb-0">
										{t(
											"elements.dropzone.dropzoneActiveLabel"
										)}
									</p>
								</div>
							) : (
								<div className="upload-dialog__dropzone-content">
									<Icon
										type="file-upload"
										className="upload__dropzone-icon"
										size="3x"
										weight="d"
									/>
									<p className="mt-3 mb-0">
										{t("elements.dropzone.dropzoneLabel", {
											type: acceptJson ? "JSON" : "XML",
										})}
									</p>
								</div>
							)}
						</div>
					</>
				</Modal>
			)}
			{isWarningOpen && (
				<Modal
					title={t(`alerts.${error ? error : "invalidFile"}.title`)}
					isOpen={isWarningOpen}
					close={handleModalClose}
					showHeaderClose={false}
					footer={
						<Button
							className="btn-primary"
							onClick={handleModalClose}
							label={t("common.labels.ok")}
						/>
					}>
					{t(`alerts.${error ? error : "invalidFile"}.description`, {
						file,
					})}
				</Modal>
			)}
		</>
	);
}
